import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { NbFormFieldModule, NbInputModule, NbListModule } from '@nebular/theme';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { InvestmentWizardService } from '../../../../../@core/utils/investment-wizard-service';
import { IInvestmentWizard } from '../wizard-interface';
import { KPI } from '../../../../../@core/api';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { AssetsService, QuantityUnitsService, ScopeService } from '../../../../../@core/utils';
import { first, map } from 'rxjs/operators';
import { AssetItemComponent } from '../../../elements/asset-item.component';
import { idMapToArray } from '../../../../../@core/utils/scope.service';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-wizard-asset-selection',
  standalone: true,
  imports: [
    NbListModule,
    CommonModule,
    NbInputModule,
    NbFormFieldModule,
    TranslateModule,
    AssetItemComponent,
    ScrollingModule,
  ],
  template: `
    <div *ngIf="filteredAssets$ | async as assets else loading" class="d-flex flex-column flex-grow-1 mx-2 pt-2">
      <nb-form-field>
        <i class="fa fa-search" nbPrefix></i>
        <input nbInput fullWidth (input)="searchQuery.next($event.target.value)" [placeholder]="'Search' | translate">
      </nb-form-field>
      <ng-container *ngIf="assets.length else noAsset">
        <cdk-virtual-scroll-viewport itemSize="120" class="asset-list-viewport">
          <div *cdkVirtualFor="let asset of assets" (click)="nextStep(asset)" class="pointer"
               [class.selected]="selectedAsset && selectedAsset.id === asset.id">
            <app-asset-item [asset]="asset"></app-asset-item>
          </div>
        </cdk-virtual-scroll-viewport>
      </ng-container>
    </div>

    <ng-template #loading>
      <div class="d-flex mt-4 justify-content-center">
        <div class="spinner-border" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>
    </ng-template>
    <ng-template #noAsset>
      <div class="d-flex mt-4 justify-content-center">
        {{ "No assets found" | translate }}
      </div>
    </ng-template>

  `,
  styleUrl: '../wizard-index.component.scss',
})
export class AssetSelectionComponent implements OnInit, IInvestmentWizard {
  readonly stateKey = 'ASSET_SELECTION';
  state = () => this.wizardService.getState(this.stateKey);

  private wizardService = inject(InvestmentWizardService);
  private assetsService = inject(AssetsService);
  private scope = inject(ScopeService);
  private destroyRef = inject(DestroyRef);
  private qu = inject(QuantityUnitsService);

  selectedAsset: KPI.Asset;

  filteredAssets$: Observable<KPI.Asset[]>;
  assetTypes$ = toSignal(this.scope.assetTypes$.pipe(idMapToArray()));
  quantityUnits$ = toSignal(this.qu.quantityUnits$.pipe(idMapToArray()));

  searchQuery = new BehaviorSubject<string>('');

  ngOnInit() {
    const site: KPI.Perimeter = this.wizardService.getState('SITE_SELECTION'),
      selectedType: KPI.InvestmentType = this.wizardService.getState('INVESTMENT_TYPE_SELECTION');
    this.selectedAsset = this.wizardService.getState('ASSET_SELECTION');

    if (!this.selectedAsset && selectedType.instruction_current_asset == KPI.InstructionAsset.Yes) {
      this.wizardService.blockCurrentStep('Please select an asset to continue');
    } else {
      this.wizardService.unblockCurrentStep();
    }

    const params: Record<string, any> = {};
    if (selectedType.replacement) {
      params.has_replacement_investment = false;
    }
    this.filteredAssets$ = combineLatest([
      this.assetsService.getSiteAsset(site.id, params).pipe(first()),
      this.searchQuery,
    ]).pipe(
      takeUntilDestroyed(this.destroyRef),
      map(([assets, query]) => {
        return assets.filter(asset => asset.label.toLowerCase().includes(query.toLowerCase()));
      }),
      map(assets => {
        return assets.map(asset => {
          const {asset_type} = asset,
            assetType = this.assetTypes$().find(at => at.id === asset_type),
            quantity_unit_id = assetType?.quantity_unit,
            quantity_unit = this.quantityUnits$().find(qu => qu.id === quantity_unit_id);
          if (quantity_unit) {
            // @ts-ignore
            asset.quantity_unit = quantity_unit;
          }
          return asset
        })
      })
    ) as Observable<KPI.Asset[]>;
  }

  nextStep(asset: KPI.Asset) {
    this.wizardService.setState('ASSET_SELECTION', asset);
    this.wizardService.next();
  }
}
